import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';

import type { ControlType, PanelInventory, Vendor } from '@pedestal-panels/contracts';
import { parsePanelInventory } from '@pedestal-panels/contracts';
import {
  aspectRatioFromMm,
  positionStyle,
  tagForControlType,
} from '@pedestal-panels/core';

import '@pedestal-panels/core';

import inventoryJson from '../inventory.json' with { type: 'json' };

/**
 * Grass Valley CGP-500 — Creative Grading Panel.
 *
 * Portrait colour-grading sibling of the OCP-400. Same vendor token
 * baseline, same shading-joystick lineage, different doctrine: where
 * the OCP-400 emits soft-button-under-display contextual editing for
 * camera shading, the CGP-500 emits page-driven grading (CGA shortcut
 * row, RGB stacked dual-concentric rotaries, joystick with three rings
 * and a purple secondary collar).
 *
 * Anatomy verified against:
 *   - Maintenance Manual 13-00089-000 v1.0 (Jan 2020), drawing
 *     11-00188-00 — chassis 84 x 354 mm front face, mounting hole
 *     centres 337.6 mm apart, 16.4 mm from each chassis edge.
 *   - Creative Grading User's Guide 13-00088-000 v1.5 (Dec 2020)
 *     pp.39–47 — front-face control layout, joystick behaviour,
 *     CGA shortcut row, RGB rotary semantics, Single/Preview/Multi
 *     row, button illumination conventions (dim blue available,
 *     bright blue selected, PREVIEW saturated blue, joystick tally
 *     blue/red/yellow).
 *   - CGP-500 Stand-Alone Usage AN-001 Rev 1 (Nov 2023) — confirms
 *     Fixed-mode local pages Light / Contrast / Colour / Enhance /
 *     Automatics / Home / Files surfaced through the CGA shortcuts.
 *   - Vendor layout SVG (`research/sources/layout/cgp-500-vector-
 *     layout.svg`, viewBox 232.441 x 1003.465) — every control
 *     position in `inventory.json` is the SVG coordinate scaled by
 *     x=3.6138, y=3.5276 into the canonical 1:10 mm viewBox (840 x
 *     3540).
 *
 * Like OCP-400, this composition iterates `inventory.json`, dispatches
 * each control through the control-type-to-tag registry, and positions
 * each primitive absolutely via percentages derived from the canonical
 * viewBox. No px in this file.
 */
export const meta = {
  id: 'cgp-500',
  vendor: 'grassvalley' as Vendor,
  model: 'CGP-500',
  productName: 'Grass Valley CGP-500 Creative Grading Panel',
} as const;

const inventory: PanelInventory = parsePanelInventory(inventoryJson);

// Map control ID → engraved icon glyph name. The CGA shortcut row
// (Light / Contrast / Colour / Enhance / Automatics / Home / Diag)
// uses text labels engraved beneath the cap on the real panel; the
// flanking nav icons (Settings, Test Signal, Recall Standard, Panel
// Lock, Call, Snapshot) and the cursor arrows are icon-only. The
// Prev/Next tabs each carry a single triangular play arrow.
//
// Buttons with no entry here fall back to their text label.
const ICON_FOR_ID: Readonly<Record<string, string>> = {
  'tab-prev': 'prev',
  'tab-next': 'next',
  'btn-settings': 'settings',
  'btn-test-signal': 'test-signal',
  'btn-recall-standard': 'recall-standard',
  'btn-panel-lock': 'panel-lock',
  'btn-call': 'call',
  'btn-snapshot': 'snapshot',
  'btn-up': 'up',
  'btn-down': 'down',
  'btn-left': 'left',
  'btn-right': 'right',
};

// IDs whose push-button cap should adopt the compact icon mode (denser
// stroke, bigger viewport fill) — i.e. small buttons in tightly packed
// rows. The CGA shortcut buttons are wider and use the default mode.
const DENSE_IDS: ReadonlySet<string> = new Set([
  'tab-prev',
  'tab-next',
  'btn-settings',
  'btn-test-signal',
  'btn-recall-standard',
  'btn-panel-lock',
  'btn-call',
  'btn-snapshot',
  'btn-up',
  'btn-down',
  'btn-left',
  'btn-right',
]);

@customElement('panel-cgp-500')
export class PanelCgp500Element extends LitElement {
  static override styles = [
    css`
      :host {
        display: block;
        inline-size: 100%;
        block-size: 100%;
        container-type: inline-size;

        /* Bridge vendor token surface to primitive variables. */
        --control-cap: var(--control-rotary-cap);
        --control-cap-highlight: color-mix(in oklab, var(--control-rotary-cap) 55%, white);
        --control-skirt: var(--control-rotary-shaft);
        --control-pointer: var(--control-button-engrave);
        --control-shaft: color-mix(in oklab, var(--control-rotary-shaft) 80%, black);
        --control-collar-primary: var(--control-rotary-shaft);
        --control-collar-secondary: #6b3aa5;
        --control-detent-line: var(--chassis-accent-2, #000);
        --chassis-recess: color-mix(in oklab, var(--chassis-colour) 60%, black);
        --tally-off: var(--illumination-off);
        --encoder-led-colour: var(--illumination-function-on, #5fa8ff);
      }

      .stage {
        display: grid;
        place-items: center;
        block-size: 100%;
        inline-size: 100%;
        padding: 1.2cqi;
        box-sizing: border-box;
      }

      .panel {
        block-size: 100%;
        aspect-ratio: var(--cgp-aspect, 84 / 354);
        max-inline-size: 100%;
        position: relative;
      }

      chassis-frame {
        block-size: 100%;
        inline-size: 100%;
        position: relative;
        --chassis-aspect: var(--cgp-aspect, 84 / 354);
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    this.dataset['vendor'] = inventory.panel.vendor;
    this.style.setProperty('--cgp-aspect', aspectRatioFromMm(
      inventory.panel.dimensionsMm.width,
      inventory.panel.dimensionsMm.height,
    ));
  }

  override render() {
    return html`
      <div class="stage">
        <div class="panel">
          <chassis-frame
            view-box=${`0 0 ${inventory.panel.canonicalViewBox.width} ${inventory.panel.canonicalViewBox.height}`}
          >
            ${inventory.controls.map((control) => this.#renderControl(control))}
          </chassis-frame>
        </div>
      </div>
    `;
  }

  #renderControl(control: PanelInventory['controls'][number]) {
    const tag = unsafeStatic(tagForControlType(control.type as ControlType));
    const positioning = positionStyle(
      control.position,
      control.dimensions,
      inventory.panel.canonicalViewBox,
    );
    const style = `${positioning};${this.#colourOverridesFor(control)}`;
    const label = control.label || nothing;
    const tallyState = this.#tallyStateFor(control);
    const tallyShape = this.#tallyShapeFor(control);
    const displayKind = this.#displayKindFor(control);
    const placeholder = this.#displayPlaceholderFor(control);
    const featureShape = this.#chassisFeatureShapeFor(control);
    const buttonState = this.#buttonStateFor(control);
    const icon = ICON_FOR_ID[control.id] ?? '';
    const dense = DENSE_IDS.has(control.id);

    if (control.type === 'chassis-feature') {
      const variant = this.#chassisFeatureVariantFor(control, featureShape);
      return staticHtml`<chassis-feature-shape
        style=${style}
        shape=${featureShape}
        variant=${variant}
        label=${control.label ?? ''}
      ></chassis-feature-shape>`;
    }

    if (control.type === 'shading-joystick') {
      // CGP-500 monoknob carries four visible illuminated bands:
      //   - upper purple tertiary collar (Master Gain)
      //   - primary black collar (Master Black) — shared with OCP-400
      //   - lower purple secondary collar (Var ColTmp)
      //   - tally ring at the base (blue idle by default, red on air,
      //     yellow ISO — driven by the tally-state attribute)
      return staticHtml`<shading-joystick
        style=${style}
        ?has-secondary-ring=${true}
        ?has-tertiary-ring=${true}
        ?has-tally-ring=${true}
        tally-state="idle"
        primary-ring-detents="32"
        secondary-ring-detents="32"
        tertiary-ring-detents="32"
      ></shading-joystick>`;
    }

    const isButton = control.type.startsWith('push-');
    const isRotary = control.type.startsWith('rotary-') || control.type === 'stacked-dual-ring-rotary';

    // RGB stacked rotaries and the central menu rotary all wear an
    // illuminated halo — the RGBs to identify the channel (the only
    // colour cue the operator sees on the unlit chassis), the menu
    // rotary to mark the focused selection while the operator scrolls
    // through CGA pages.
    const ledRing = isRotary && (
      control.id.startsWith('rotary-rgb-') ||
      control.id === 'rotary-nav'
    );
    const illuminated = ledRing;

    return staticHtml`<${tag}
      style=${style}
      label=${icon ? '' : label}
      icon=${icon || ''}
      ?dense=${dense}
      state=${isButton ? buttonState : tallyState ?? ''}
      ?led-ring=${ledRing}
      ?illuminated=${illuminated}
      shape=${tallyShape ?? ''}
      kind=${displayKind ?? ''}
      placeholder=${placeholder ?? ''}
      mode=${displayKind ? 'placeholder' : ''}
    ></${tag}>`;
  }

  /**
   * Drive each push-button's lit colour and each rotary's LED-ring
   * colour from the inventory's illumination block:
   *
   *   - The RGB stacked rotaries (`rotary-rgb-*`) carry the channel
   *     colour in `illumination.colours[0].hex`; we map it into
   *     `--re-led` so the halo identifies the channel without any
   *     vendor-token branching.
   *   - Other rotaries use the default function-blue from the panel-
   *     scoped `--encoder-led-colour` block above.
   *   - Buttons feed their colour into `--pb-led` — the CGP-500 idiom
   *     is dim blue available / bright blue selected for every soft
   *     button, with PREVIEW carrying a saturated blue and the
   *     joystick-tally a separate red/yellow/blue family.
   *   - Displays do not need an overlay; the LCD palette is fixed by
   *     `kind="lcd-colour"` on the primitive.
   */
  #colourOverridesFor(control: PanelInventory['controls'][number]): string {
    const litColour = control.illumination?.colours?.[0]?.hex;
    if (!litColour) return '';
    if (control.id.startsWith('rotary-rgb-')) return `--re-led:${litColour}`;
    if (control.type.startsWith('rotary-')) return `--re-led:${litColour}`;
    if (control.type.startsWith('display-')) return '';
    if (control.type.startsWith('push-')) return `--pb-led:${litColour}`;
    return '';
  }

  /**
   * Map the inventory's `illumination.defaultState` to the push-button
   * primitive's `state` attribute.
   *
   *   - `on`              -> `default` (soft glow)
   *   - `state-dependent` -> `off` until something tells the panel
   *                          otherwise (cursor arrows, menu rotary
   *                          halo at idle)
   *   - `off`             -> `off`
   */
  #buttonStateFor(control: PanelInventory['controls'][number]): string {
    const defaultState = control.illumination?.defaultState;
    if (defaultState === 'on') return 'default';
    return 'off';
  }

  #chassisFeatureShapeFor(
    control: PanelInventory['controls'][number],
  ): 'screw' | 'slot' | 'section' | 'legend' {
    if (control.id.includes('screw')) return 'screw';
    if (control.id.includes('slot')) return 'slot';
    if (
      control.id.includes('section') ||
      control.id.includes('plate') ||
      control.id.includes('lip')
    )
      return 'section';
    return 'legend';
  }

  /**
   * Pick the variant attribute for a chassis-feature element. The
   * CGP-500 vocabulary mirrors the OCP-400: the upper plate sits as
   * a `section-plate` (lighter raised mounting plate); the joystick
   * lip is the `section-recess` three-layer dike; engraved chassis
   * legends render as `engraved` so the CGA Assignment header reads
   * as off-white-on-dark rather than the OCP-400 amber paint.
   */
  #chassisFeatureVariantFor(
    control: PanelInventory['controls'][number],
    shape: 'screw' | 'slot' | 'section' | 'legend',
  ): 'engraved' | 'amber' | 'section-deep' | 'section-recess' | 'section-plate' {
    if (shape === 'section') {
      if (control.id.includes('lip')) return 'section-recess';
      if (control.id.includes('plate')) return 'section-plate';
    }
    return 'engraved';
  }

  #tallyStateFor(control: PanelInventory['controls'][number]): string | null {
    if (control.type !== 'led-rgb' && control.type !== 'led-discrete') return null;
    return 'off';
  }

  #tallyShapeFor(control: PanelInventory['controls'][number]): string | null {
    if (control.type !== 'led-rgb' && control.type !== 'led-discrete') return null;
    return 'stripe';
  }

  /**
   * The CGP-500 carries a single colour LCD on its upper third.
   * `kind="lcd-colour"` selects the dark-glass / white-text palette;
   * the placeholder dataset `cgp-grading` renders the macro-photo
   * layout (tally bar, hero row, REF strip, 4x2 value-card grid).
   */
  #displayKindFor(
    control: PanelInventory['controls'][number],
  ): 'lcd-colour' | null {
    if (!control.type.startsWith('display-')) return null;
    if (control.id === 'lcd-display') return 'lcd-colour';
    return null;
  }

  #displayPlaceholderFor(control: PanelInventory['controls'][number]): string | null {
    if (control.id === 'lcd-display') return 'cgp-grading';
    return null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'panel-cgp-500': PanelCgp500Element;
  }
}
